import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { StateService } from '../../../services/state/stateService';

/*--------------------------------------------------------------------------------------------
* EN: Hook to manage incidence statuses fetching and transformation logic.
* ES: Hook para gestionar la lógica de obtención y transformación de estados de incidencias.
--------------------------------------------------------------------------------------------*/

const useIncidenceStatuses = (initialFilters = {}) => {

    const { user } = useSelector((state: RootState) => state.auth);
    const [data, setData] = useState<any[]>([]);
    const [filters, setFilters] = useState<Record<string, any>>({
        limit: 999999,
        filter_filters: { ...initialFilters, entity: 'incidence' }
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const service = new StateService();
            let requestFilters = { ...filters };
            if (user?.id && !user.department) {
                requestFilters = {
                    ...requestFilters,
                    filter_filters: { ...requestFilters.filter_filters, ignore_department_filter: true }
                };
            }
            const response = await service.listStatusesForSelect(requestFilters);
            const raw = response.getResponseData();
            // API puede devolver { data: { data: [...] } } o { data: [...] }
            const statusesArray = Array.isArray(raw?.data) ? raw.data : raw?.data?.data;

            if (statusesArray?.length) {
                const toBool = (v: unknown) => [true, 't', 1, '1', 'true'].includes(v as never);
                const mappedData = statusesArray.map((item: any) => ({
                    value: item.id,
                    label: item.name,
                    color: item.color,
                    initialStatus: item.initialStatus,
                    draftStatus: item.draftStatus,
                    satisfactoryStatus: toBool(item.satisfactoryStatus ?? item.satisfactory_status),
                    unsatisfactoryStatus: toBool(item.unsatisfactoryStatus ?? item.unsatisfactory_status),
                    requiresKoReason: toBool(item.requiresKoReason ?? item.requires_ko_reason),
                }));
                setData(mappedData);
            }
        } catch (error) {
            console.log('Error fetching incidence statuses:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        fetchData();
    }, [filters, user?.department]);

    const updateHookFilters = (newFilters: any) => {
        setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
    };

    const getList = (draftStatuses?: boolean, entity?: string) => {
        let filteredData = draftStatuses ? data : data.filter((state: any) => !state.draftStatus);
        if (entity) return filteredData.filter((state: any) => state.value !== entity);
        return filteredData;
    };

    const getEntity = (id: string) => {
        return data.find((entity: any) => entity.value === id);
    };

    return {
        fetchIncidenceStatusesList: fetchData,
        updateIncidenceStatusesHookFilters: updateHookFilters,
        getIncidenceStatusesList: getList,
        getIncidenceStatus: getEntity,
        isLoadingIncidenceStatuses: isLoading
    };
}

export default useIncidenceStatuses;
